<template>
  <div class="w-full">
    <!-- Section label -->
    <p class="text-[11px] text-gray-400 font-semibold uppercase tracking-widest mb-1">
      {{ $t('WeekDetailPanel.sectionTitle') || 'YOUR RESULTS IN DETAIL' }}
    </p>

    <!-- Title row: date on left, Gesamtmittelwert on right -->
    <div class="flex items-baseline justify-between mb-5">
      <h3 class="text-gray-900 text-lg lg:text-xl font-bold">
        {{ formattedDate }}
      </h3>
      <div v-if="gesamtmittelwert !== null" class="text-right flex-shrink-0 ml-4">
        <span class="text-[11px] text-gray-400 font-medium block">
          {{ $t('WeekDetailPanel.gesamtmittelwert') || 'Overall Mean' }}
        </span>
        <span class="text-2xl font-bold" :style="{ color: getScoreColor(gesamtmittelwert) }">
          {{ formatKpi(gesamtmittelwert) }}
        </span>
        <span class="text-sm text-gray-400 font-medium">/ {{ maxScale }}</span>
      </div>
    </div>

    <!-- WERTE NACH DIMENSION -->
    <div v-if="hasKpiValues" class="mb-6">
      <p class="text-[11px] text-gray-400 font-semibold uppercase tracking-widest mb-3">
        {{ $t('WeekDetailPanel.dimensionValues') || 'VALUES BY DIMENSION' }}
      </p>
      <div class="flex flex-row gap-4 flex-wrap">
        <div
          class="flex flex-col flex-1 min-w-[11rem] max-w-fit sm:min-w-[12rem]"
          v-for="(value, key) in selectedData.kpiValues"
          :key="key"
        >
          <!-- Top line: thin, full-width — colors match chart config by dimension key -->
          <div
            class="h-[2px] w-full"
            :style="{ backgroundColor: getDimensionColorByName(key) }"
          />
          <!-- Label + value: min width + flex keeps number from overlapping long labels -->
          <div class="flex items-baseline justify-between gap-2 py-1 px-0.5 min-w-0">
            <span class="text-[15px] text-gray-600 flex-1 min-w-0 break-words leading-snug pr-1">
              {{ getDimensionLabel(key) }}
            </span>
            <span
              class="text-2xl font-bold flex-shrink-0 tabular-nums"
              :style="{ color: getDimensionColorByName(key) }"
            >
              {{ formatKpi(value) }}
            </span>
          </div>
          <!-- Bottom bar: grey track with colored fill -->
          <div class="relative h-[5px] w-full rounded-sm bg-gray-200">
            <div
              class="absolute top-0 left-0 h-full rounded-sm transition-all duration-500"
              :style="{
                width: `${Math.min(100, (Number(value) / maxScale) * 100)}%`,
                backgroundColor: getDimensionColorByName(key),
              }"
            />
          </div>
        </div>
      </div>
    </div>

    <!-- QUALITATIVE EINORDNUNG (all open text answers) -->
    <div
      v-if="openAnswersList.length > 0"
      class="mb-4"
    >
      <p class="text-[11px] text-gray-400 font-semibold uppercase tracking-widest mb-3">
        {{ $t('WeekDetailPanel.qualitativeTitle') || 'QUALITATIVE ASSESSMENT' }}
      </p>
      <div class="grid grid-cols-1 md:grid-cols-2 gap-4">
        <div
          v-for="(item, idx) in openAnswersList"
          :key="item.questionId || idx"
          class="bg-white border border-gray-200 rounded-lg p-4"
        >
          <div class="flex items-center gap-2 mb-2">
            <span
              v-if="item.dimensionName"
              class="text-xs font-semibold px-2 py-0.5 rounded-full text-white"
              :style="{ backgroundColor: getDimensionColorByName(item.dimensionName) }"
            >
              {{ getDimensionLabel(item.dimensionName) }}
            </span>
          </div>
          <p class="text-[15px] text-gray-500 mb-1.5">
            {{ getQuestionText(item.questionText) }}
          </p>
          <div
            v-if="item.answer != null && item.answer !== ''"
            class="pl-3 border-l-2"
            :style="{ borderColor: getDimensionColorByName(item.dimensionName) || '#94a3b8' }"
          >
            <p
              class="text-[16px] text-gray-800"
              :class="isAnswerExpanded(idx) ? '' : 'line-clamp-5'"
            >
              {{ item.answer }}
            </p>
            <button
              v-if="item.answer.length > 300"
              class="text-sm text-blue-600 hover:text-blue-800 mt-1.5
               font-medium cursor-pointer"
              @click="toggleAnswer(idx)"
            >
              {{ isAnswerExpanded(idx)
                ? ($t('WeekDetailPanel.seeLess') || 'See less')
                : ($t('WeekDetailPanel.seeMore') || 'See more') }}
            </button>
          </div>
          <p
            v-else
            class="text-[16px] text-gray-400 italic"
          >
            {{ $t('WeekDetailPanel.noAnswer') || 'Keine Angabe' }}
          </p>
        </div>
      </div>
    </div>

    <!-- Empty state -->
    <div
      v-if="openAnswersList.length === 0 && !hasKpiValues"
      class="text-sm text-gray-400 italic py-4 text-center"
    >
      {{ $t('WeekDetailPanel.noStickyAnswers')
        || 'No data available for the selected period.' }}
    </div>
  </div>
</template>

<script>
import { formatLocaleDate } from '@/helpers/formatLocaleDate';
import { formatOwnResultNumber } from '@/helpers/formatOwnResultNumber';

const DIMENSION_COLORS = [
  '#3b82f6', '#22c55e', '#a855f7', '#f97316', '#06b6d4',
  '#ec4899', '#eab308', '#6366f1',
];

export default {
  name: 'WeekDetailPanel',
  props: {
    selectedData: {
      type: Object,
      required: true,
    },
    dimensions: {
      type: Array,
      default: () => [],
    },
    dimensionColors: {
      type: Object,
      default: () => ({}),
    },
    maxScale: {
      type: Number,
      default: 5,
    },
  },
  data() {
    return {
      expandedAnswers: new Set(),
    };
  },
  watch: {
    selectedData() {
      this.expandedAnswers = new Set();
    },
  },
  computed: {
    formattedDate() {
      const dateStr = this.selectedData?.completedAt || this.selectedData?.dateLabel;
      if (!dateStr) return '';
      const date = new Date(dateStr);
      if (Number.isNaN(date.getTime())) return dateStr;
      return formatLocaleDate(date, this.$i18n?.locale);
    },

    gesamtmittelwert() {
      if (!this.hasKpiValues) return null;
      const values = Object.values(this.selectedData.kpiValues)
        .map(Number)
        .filter((v) => !Number.isNaN(v));
      if (values.length === 0) return null;
      const avg = values.reduce((sum, v) => sum + v, 0) / values.length;
      return Math.round(avg * 100) / 100;
    },

    hasKpiValues() {
      return this.selectedData?.kpiValues
        && Object.keys(this.selectedData.kpiValues).length > 0;
    },

    openAnswersList() {
      const open = this.selectedData?.openAnswers || [];
      const sticky = this.selectedData?.stickyAnswers || [];
      const seenIds = new Set(open.map((a) => a.questionId).filter(Boolean));
      const uniqueSticky = sticky.filter(
        (a) => (!a.questionId || !seenIds.has(a.questionId))
          && a.type !== 'rangeSlider',
      );
      const allAnswers = [...open, ...uniqueSticky];
      return allAnswers.filter((a) => a.type !== 'rangeSlider');
    },
  },
  methods: {
    getDimensionLabel(dimensionValue) {
      const dimension = this.dimensions.find((d) => d.value === dimensionValue);
      const locale = this.$i18n?.locale || 'en';
      return dimension?.translation?.[locale] || dimensionValue;
    },

    /**
     * Match BarChartComponent / LineChartComponent hex handling.
     */
    normalizeDisplayColor(color) {
      if (color == null || color === '') return null;
      if (typeof color !== 'string') return color;
      const s = color.trim();
      if (s.startsWith('#') || s.startsWith('rgb') || s.startsWith('hsl')) return s;
      return `#${s}`;
    },

    getDimensionColorByName(dimensionName) {
      if (!dimensionName) return DIMENSION_COLORS[0];
      const map = this.dimensionColors || {};
      if (map[dimensionName]) {
        return this.normalizeDisplayColor(map[dimensionName]);
      }
      const keys = Object.keys(this.selectedData?.kpiValues || {});
      const idx = keys.indexOf(dimensionName);
      if (idx >= 0) {
        const fromParentIdx = map[`__idx_${idx}`];
        if (fromParentIdx) return this.normalizeDisplayColor(fromParentIdx);
        return DIMENSION_COLORS[idx % DIMENSION_COLORS.length];
      }
      return DIMENSION_COLORS[0];
    },

    getQuestionText(textObj) {
      if (!textObj) return '';
      const locale = this.$i18n?.locale || 'en';
      return textObj[locale] || textObj.en || textObj.de || '';
    },

    formatKpi(value) {
      return formatOwnResultNumber(value);
    },

    getScoreColor(value) {
      const num = Number(value);
      if (Number.isNaN(num)) return '#475569';
      if (num >= 4) return '#2563EB';
      if (num >= 3) return '#475569';
      return '#F59E0B';
    },

    isAnswerExpanded(idx) {
      return this.expandedAnswers.has(idx);
    },

    toggleAnswer(idx) {
      const next = new Set(this.expandedAnswers);
      if (next.has(idx)) {
        next.delete(idx);
      } else {
        next.add(idx);
      }
      this.expandedAnswers = next;
    },
  },
};
</script>
